// Setup — a company's five setup steps, from the Companies list (approved
// mockup "Add Company Wizard", tab "Fixing a setup later", 2026-10-11). The
// state lives on the server (registry_companies.activation_log), so closing
// the Add company panel or refreshing loses nothing: the row's "Setup failed"
// tag or its Setup button opens this again.
//
// Each step can be run again and every run is safe to repeat (CITS-Server
// trialActivation.service.js runActivationStep): a failed step offers "Try
// this step again" and the steps after it then finish on their own.
import { useCallback, useEffect, useState } from 'react'
import api from '../../api/axios'
import { ArrowPathIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { generatePassword } from './AddCompanyPanel'

export interface SetupCompany {
  id: string
  company_name: string
}

interface Step {
  key: 'record' | 'features' | 'database' | 'login' | 'email'
  label: string
  status: 'waiting' | 'running' | 'done' | 'failed'
  at?: string | null
  error?: string | null
  skipped?: boolean
  sentAt?: string
  passwordSetAt?: string
  linkSentAt?: string
}
interface Activation {
  tracked: boolean
  state: 'waiting' | 'building' | 'ready' | 'failed' | 'held' | 'declined'
  steps: Step[]
  failedStep?: string | null
  error?: string | null
}

interface Props {
  company: SetupCompany | null
  onClose: () => void
  /** Something changed (refresh the list's tags). */
  onChanged?: () => void
}

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '')
const btn = 'px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 whitespace-nowrap disabled:opacity-50'

export default function SetupPanel({ company, onClose, onChanged }: Props) {
  const [activation, setActivation] = useState<Activation | null>(null)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [pwOpen, setPwOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    if (!company) return
    try {
      const { data } = await api.get(`/super-admin/companies/${company.id}/activation`)
      setActivation(data)
      setLoadError('')
    } catch (e: any) {
      setLoadError(e.response?.data?.message || 'Could not read setup progress')
    }
  }, [company])

  useEffect(() => {
    setActivation(null); setError(''); setNotice(''); setPwOpen(false); setPassword(''); setBusy(null)
    if (company) load()
  }, [company, load])

  // Poll while something is running.
  const running = activation?.state === 'building'
  useEffect(() => {
    if (!company || !running) return
    const t = setInterval(load, 1500)
    return () => clearInterval(t)
  }, [company, running, load])

  // A run that just finished: let the list update its tag.
  const [wasRunning, setWasRunning] = useState(false)
  useEffect(() => {
    if (running) setWasRunning(true)
    else if (wasRunning) { setWasRunning(false); onChanged?.() }
  }, [running, wasRunning, onChanged])

  const run = async (step: Step['key'], opts: { owner_password?: string; done?: string } = {}) => {
    if (!company) return
    setBusy(step); setError(''); setNotice('')
    try {
      await api.post(`/super-admin/companies/${company.id}/activation/run`, { step, owner_password: opts.owner_password })
      if (opts.done) setNotice(opts.done)
      // Show it running now; polling picks up the server's progress.
      setActivation((a) => a && { ...a, state: 'building', steps: a.steps.map((x) => (x.key === step ? { ...x, status: 'running', error: null } : x)) })
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not run that step')
    } finally {
      setBusy(null)
    }
  }

  const savePassword = async () => {
    await run('login', { owner_password: password, done: 'New password saved. Hand it to the owner; it is not shown again.' })
    setPwOpen(false); setPassword('')
  }

  if (!company) return null
  const steps = activation?.steps || []
  const failed = activation?.state === 'failed'
  const ready = activation?.state === 'ready'
  const untracked = activation && !activation.tracked

  const subtitle = (s: Step) => {
    if (s.status === 'failed') return <span className="text-red-600 font-medium break-words">{s.error || 'Failed'}</span>
    if (s.status === 'running') return 'Running…'
    if (s.status === 'waiting') return 'Not run yet'
    if (s.key === 'email') return s.skipped ? 'Skipped' : `Sent ${when(s.sentAt || s.at)}`
    if (s.key === 'login') {
      if (s.passwordSetAt) return `Password set ${when(s.passwordSetAt)}`
      if (s.linkSentAt) return `Setup link sent ${when(s.linkSentAt)}`
    }
    return `Done ${when(s.at)}`
  }

  const actions = (s: Step) => {
    if (running || s.status === 'running' || s.status === 'waiting' || s.key === 'record') return null
    if (s.status === 'failed') {
      return (
        <button type="button" disabled={!!busy} onClick={() => run(s.key)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 whitespace-nowrap disabled:opacity-50">
          <ArrowPathIcon className="w-3.5 h-3.5" />Try this step again
        </button>
      )
    }
    if (s.key === 'features') return <button type="button" className={btn} disabled={!!busy} onClick={() => run('features', { done: 'Plan re-applied.' })}>Re-apply plan</button>
    if (s.key === 'database') return <button type="button" className={btn} disabled={!!busy} onClick={() => run('database', { done: 'Database checked.' })}>Check database</button>
    if (s.key === 'login') {
      return (
        <>
          <button type="button" className={btn} disabled={!!busy} onClick={() => { setPwOpen((v) => !v); if (!password) setPassword(generatePassword()) }}>Set a new password</button>
          <button type="button" className={btn} disabled={!!busy} onClick={() => run('login', { done: 'Setup link sent to the owner.' })}>Send setup link</button>
        </>
      )
    }
    if (s.key === 'email') return <button type="button" className={btn} disabled={!!busy} onClick={() => run('email', { done: 'Welcome email sent.' })}>{s.skipped ? 'Send now' : 'Send again'}</button>
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/45" onClick={onClose} />
      <div role="dialog" aria-label={`Setup for ${company.company_name}`} className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <h2 className="flex-1 text-lg font-bold text-slate-900 truncate">Setup · {company.company_name}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-slate-100">
            <XMarkIcon className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loadError && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{loadError}</div>}
          {!activation && !loadError && <p className="text-sm text-slate-500">Loading…</p>}

          {untracked && (
            <p className="text-sm text-slate-600 leading-relaxed">
              This company was set up before setup tracking, so there are no steps to run here. Use Edit, Company features and the admin list for changes.
            </p>
          )}

          {activation && !untracked && (
            <>
              <p className={`text-sm font-semibold ${failed ? 'text-red-600' : ready ? 'text-green-700' : 'text-slate-600'}`}>
                {failed
                  ? 'One step failed. Fix the cause, then run that step again. The steps after it run on their own once it works.'
                  : running ? 'Running…'
                  : ready ? 'Everything is set up. You can run any step again; each is safe to repeat.'
                  : 'Setup has not finished.'}
              </p>
              <ol className="space-y-2.5">
                {steps.map((s, i) => (
                  <li key={s.key} className={`flex gap-3 items-start rounded-xl border p-3 ${s.status === 'failed' ? 'border-red-300 bg-red-50/60' : 'border-slate-200'}`}>
                    <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold shrink-0 ${
                      s.status === 'done' ? 'bg-green-600 text-white' : s.status === 'running' ? 'bg-blue-600 text-white animate-pulse motion-reduce:animate-none' : s.status === 'failed' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {s.status === 'done' ? <CheckIcon className="w-3.5 h-3.5" /> : s.status === 'failed' ? '!' : i + 1}
                    </span>
                    <span className="flex-1 min-w-0 text-sm">
                      <span className="block font-semibold text-slate-900">{s.label}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">{subtitle(s)}</span>
                      {s.key === 'login' && pwOpen && (
                        <span className="flex gap-2 mt-2">
                          <input
                            aria-label="New password"
                            className="flex-1 min-w-0 h-9 px-2.5 rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <button type="button" className={btn} onClick={() => setPassword(generatePassword())}>Generate</button>
                          <button type="button" className={btn} disabled={password.length < 12 || !!busy} onClick={savePassword}>Save</button>
                        </span>
                      )}
                      {s.key === 'login' && pwOpen && password.length < 12 && <span className="block text-xs text-slate-500 mt-1">At least 12 characters.</span>}
                    </span>
                    <span className="flex flex-col gap-1.5 items-end">{actions(s)}</span>
                  </li>
                ))}
              </ol>
            </>
          )}

          {notice && <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">{notice}</div>}
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>
  )
}
